import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Sidebar } from "@/components/Sidebar";
import { NotificationBell } from "@/components/NotificationBell";
import { Today } from "@/views/Today";
import { Wellness } from "@/views/Wellness";
import { Book } from "@/views/Book";
import { AICourse } from "@/views/AICourse";
import { WeeklyPlan } from "@/views/WeeklyPlan";
import { CalendarView } from "@/views/CalendarView";
import { Settings } from "@/views/Settings";
import { Users } from "@/views/Users";
import { AdminOverview } from "@/views/AdminOverview";
import { Auth } from "@/views/Auth";
import { Pending } from "@/views/Pending";
import { ROLE_VIEWS, ROLE_SECTORS } from "@/lib/roles";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";

const TITLES = {
  today: { t: "Oggi", s: "Il tuo riepilogo giornaliero" },
  overview: { t: "Panoramica Globale", s: "Tutti i clienti e le performance del team" },
  wellness: { t: "Business Wellness & Attività Indipendente", s: "Reclutamento, formazione, clienti e assistenza" },
  book: { t: "Progetto Libro & Autore", s: "Recensioni, estratti e storytelling" },
  ai: { t: "AI allo Specchio", s: "Funnel, prompt, immagini e micro-tutorial" },
  plan: { t: "Piano settimanale AI", s: "Un mini-calendario di contenuti per tutta la settimana" },
  calendar: { t: "Calendario", s: "I tuoi contenuti" },
  settings: { t: "Impostazioni · I Miei Link", s: "I tuoi riferimenti personali per i messaggi automatici" },
  users: { t: "Gestione Utenti & Ruoli", s: "Approva gli accessi e assegna i permessi" },
};

function Studio() {
  const { user } = useAuth();
  const allowedViews = ROLE_VIEWS[user.role] || [];
  const allowedSectors = ROLE_SECTORS[user.role] || [];
  const [view, setView] = useState(allowedViews[0] || "today");
  const activeView = allowedViews.includes(view) ? view : (allowedViews[0] || "today");
  const meta = TITLES[activeView] || { t: "", s: "" };
  const showBell = user.role === "admin" || user.role === "wellness";

  return (
    <div className="App flex min-h-screen bg-grid">
      <Sidebar view={activeView} onChange={setView} allowedViews={allowedViews} />
      <main className="flex-1 min-w-0 pb-20 lg:pb-0">
        <header className="sticky top-0 z-30 bg-[#0a0b10]/80 backdrop-blur-xl border-b border-white/10 px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-display font-extrabold text-xl sm:text-2xl truncate">{meta.t}</h1>
            <p className="text-slate-400 text-sm truncate">{meta.s}</p>
          </div>
          {showBell && <NotificationBell onNavigate={setView} user={user} />}
        </header>
        <AnimatePresence mode="wait">
          <motion.div key={activeView} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
            {activeView === "today" && <Today onNavigate={setView} allowedSectors={allowedSectors} showContacts={showBell} />}
            {activeView === "overview" && <AdminOverview />}
            {activeView === "wellness" && <Wellness onNavigate={setView} />}
            {activeView === "book" && <Book />}
            {activeView === "ai" && <AICourse />}
            {activeView === "plan" && <WeeklyPlan onNavigate={setView} />}
            {activeView === "calendar" && <CalendarView allowedSectors={allowedSectors} />}
            {activeView === "settings" && <Settings />}
            {activeView === "users" && <Users />}
          </motion.div>
        </AnimatePresence>
      </main>
      <Toaster position="top-right" theme="dark" richColors />
    </div>
  );
}

function Gate() {
  const { user } = useAuth();
  if (user === undefined) {
    return <div className="min-h-screen bg-grid grid place-items-center"><Loader2 className="h-7 w-7 animate-spin text-slate-500" /></div>;
  }
  if (user === null) {
    return <><Auth /><Toaster position="top-right" theme="dark" richColors /></>;
  }
  if (user.role === "pending" || !user.approved) {
    return <><Pending /><Toaster position="top-right" theme="dark" richColors /></>;
  }
  return <Studio />;
}

function App() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}

export default App;
